<template>
  <!-- map -->
  <div class='map'>
    <div class='map__container flex'>
      <canvas class='map__canvas' />
    </div>

    <div class='legend__parent flex flex-justify-end'>
      <div class='legend__child'>
        <div class='legend__title'>
          <span v-if="lang === 'en'">legend</span>
          <span v-else>leyenda</span>
        </div>

        <hr>

        <!-- bridge -->
        <div class='legend__item flex flex-justify-start flex-direction-row'>
          <svg class='legend__icon'>
            <circle cx="50%" cy="50%" r=".5rem" stroke="#009149" stroke-width="5" fill="none" />
          </svg>
          <p class='legend__text'>
            <span v-if="lang === 'en'">bridge</span>
            <span v-else>puente</span>
          </p>
        </div>

        <!-- village -->
        <div class='legend__item flex flex-justify-start flex-direction-row'>
          <svg class='legend__icon'>
            <circle cx="50%" cy="50%" r=".33rem" stroke="#161345" stroke-width="3" fill="none" />
          </svg>
          <p class='legend__text'>
            <span v-if="lang === 'en'">village</span>
            <span v-else>pueblo</span>
          </p>
        </div>

        <!-- trip -->
        <div class='legend__item flex flex-justify-start flex-direction-row'>
          <svg class='legend__icon'>
            <circle cx="50%" cy="50%" r=".25rem" fill="#161345" fill-opacity=".33" />
          </svg>
          <p class='legend__text'>
            <span v-if="lang === 'en'">trip</span>
            <span v-else>viaje</span>
            <span v-if="['education', 'employment', 'healthcare'].includes(currentTripPurpose)"> | 
              <span :class='currentTripClass'>
                <span v-if="lang==='en'">{{ currentTripPurpose }}</span>
                <span v-else>{{ currentTripPurposeEs }}</span>
              </span>
            </span>
          </p>
        </div>


      </div>
    </div>
  </div>
</template>


<script>

import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

import { geoPath, geoMercator } from 'd3-geo';
          
import { plot } from '../js/map.js';

export default {
  props: {
    rawData: Object,
    dataIsReady: Boolean,
    resize: Number,
    isMobile: Boolean,
    lang: String,
  },
  data() {
    return {
      data: {
        countries: [],
        villages: [],
        bridges: [],
        trips: []
      },

      config: {},

      canvas: null,
      context: null,
      projection: null,
      path: null,

      width: 0,
      height: 0,

      currentTripPurpose: '',
      currentTripPurposeEs: '',
      currentTripClass: '',

      earthCoords: [0,0],
      primaryBridgeCoords: [29.65, -2.635482],
      rwandaEndCoords: [29.6215, -2.3043],
      // rwandaEndCoords: [29.8739, -2.0],

      villagesGahororo: [
          'Uwimpundu', 'Rugege', 'Cyetete', 'Umuyange',
          'Rwezamenyo', 'Kinani', 'Agatenderi', 'Akarehe',
          'Ikigarama', 'Munanira', 'Kibingo', 'Butare',
          'Kaburemera', 'Gikombe', 'Uwarugondo', 'Akabacura',
          'Kibu', 'Buye', 'Nyarutovu', 'Busoro', 'Nyarwumba',
          'Byimana', 'Gikomero', 'Ngeri', 'Nkoto',
        ],
    }
  },
  computed: {
    rwandaStartCoords() {
      return this.isMobile ? [29.8739, -2.4] : [29.8739, -2.1]
    },
    earthScale() {
      return this.isMobile ? 200 : 400
    },
    rwandaStartScale() {
      return this.isMobile ? 25000 : 30000
    },
    primaryBridgeScale() {
      return this.isMobile ? 1200000 : 900000
    },
    midRwandaScale() {
      return this.isMobile ? 120000 : 120000
    },
    rwandaEndScale() {
      return this.isMobile ? 50000 : 50000
    },
    midEarthScale() {
      return this.isMobile ? 300 : 3000
    },
    countryLineWidth() {
      return {
        max: this.isMobile ? 25 : 20,
        min: this.isMobile ? 5 : 3
      }
    },
    villageRadius() {
      return {
        max: this.isMobile ? 15 : 10,
        min: this.isMobile ? 0 : 0
      }  
    },
    highlightRadius() {
      return {
        min: this.isMobile ? 15 : 10,
        max: this.isMobile ? 550 : 450
      }
    },
    bridgeRadius() {
      return {
        min: this.isMobile ? 10 : 5,
        max: this.isMobile ? 30 : 20,
      }
    },
    tripRadius() {
      return {
        max: this.isMobile ? 10 : 5,
        min: this.isMobile ? 7.5 : 2.5
      }
    },
  },
  watch: {
    resize() {
      this.handleResize();
    },
    dataIsReady() {
      if (this.dataIsReady === true) {
        // assign prop to var so you can mutate it
        this.config = {          
          highlightRadiusMin: this.highlightRadius.min,
          highlightRadiusMax: this.highlightRadius.max,
          highlightRadiusPct: 0,
          countryLineWidth: this.countryLineWidth.max,
          bridgeRadius: 0,
          villageRadius: 0,
          tripRadius: this.tripRadius.max,
        }

        // load the data changes
        this.scrollSetDataOnLoad();
        this.scrollSetDataForBridge();
        this.scrollSetDataForRwanda();
        this.scrollSetDataForWorld();

        // setup scrollTriggers once there is data
        this.initScrollLoadMap();
        this.initScrollFirstBridge();
        this.initScrollFirstBridgeZoom();
        this.initScrollHighlightRoutes();
        this.initScrollHighlightVillages();
        this.initScrollHighlightRoutesAll();
        this.initScrollWorld();

        // load the moving trips
        this.initScrollTrips();
      }
    },
  },
  methods: {
    handleResize() {
      // get the canvas
      this.canvas = document.querySelector('.map__canvas');
      this.context = this.canvas.getContext('2d');

      // update canvas resolution
      const resolution = window.devicePixelRatio || 1;
      this.context.scale(resolution, resolution);

      // update canvas size to match CSS and to incorporate resolution
      this.canvas.width = this.canvas.clientWidth * resolution;
      this.canvas.height = this.canvas.clientHeight * resolution;
      
      // use these sizes for plotting
      this.width = this.canvas.width;
      this.height = this.canvas.height;
    },
    setActiveCountries() {
      // default to all countries
      let countries = Object.assign([], this.rawData.countries);
      
      // if it's in the config, filter
      if (Object.keys(this.config).includes('countries')) {
        countries = countries.filter( x => {
          return this.config.countries.includes(x.properties.name)
        })
      }

      // save
      this.data.countries = countries;
    },
    setActiveVillages() {
      // default to all
      let villages = Object.assign([], this.rawData.villages);

      // if it's in config, filter
      if (Object.keys(this.config).includes('villages')) {
        villages = villages.filter( x => {
          return this.config.villages.includes(x.name)
        })
      }

      // save
      this.data.villages = villages;
    },
    setActiveBridges() {
      // default to all
      let bridges = Object.assign([], this.rawData.bridges);

      // if it's in config, filter
      if (Object.keys(this.config).includes('bridges')) {
        bridges = bridges.filter( x => {
          return this.config.bridges.includes(x.bridge_id)
        })
      }
      // save
      this.data.bridges = bridges;
    },
    setActiveTrips() {
      // default to all
      let trips = Object.assign([], this.rawData.trips);

      // if config asks for bridgeTrips, then filter
      if (Object.keys(this.config).includes('bridgeTrips')) {
        trips = trips.filter(x => { return this.config.bridgeTrips.includes(x.bridge_id);
        })
      }

      // save
      this.data.trips = trips;
    },
    setData() {
      this.setActiveCountries();
      this.setActiveVillages();
      this.setActiveBridges();
      this.setActiveTrips();
    },
    draw() {
      // clear current drawing
      this.context.clearRect(0, 0, this.width, this.height);

      // update projection
      this.updateProjection(this.config.center, this.config.scale);
      this.updatePath();

      // send to external plotting function
      plot(this.config, this.context, this.projection, this.path, this.data)
    },
    updateTrips() {
      const progressIncrement = .004;

      // update point positions (interpolate between source and target)
      for (let i=0; i<this.data.trips.length; i++) {
      // for (let i=0; i<1; i++) {
        let trip = Object.assign({}, this.data.trips[i]);

        // each point starts at a different 'progress' on their trip; update their progress
        const curProgress = (trip.progress + progressIncrement);
        trip.progress = curProgress > 1 ? curProgress - 1 : curProgress;


        // update current position
        if (trip.progress <= .5) {
          const legProgress = trip.progress / .5;
          trip.current_lat = trip.home_lat * (1 - legProgress) + trip.bridge_lat * legProgress;
          trip.current_lon = trip.home_lon * (1 - legProgress) + trip.bridge_lon * legProgress;
        } else {
          const legProgress = (trip.progress - .5) / .5;
          trip.current_lat = trip.bridge_lat * (1 - legProgress) + trip.dest_lat * legProgress;
          trip.current_lon = trip.bridge_lon * (1 - legProgress) + trip.dest_lon * legProgress;
        }
        this.data.trips[i] = Object.assign({}, trip);
      }
    },
    updateProjection(center, scale) {
      this.projection = geoMercator()
        .center(center)
        .scale(scale)
        .translate([ this.width/2, this.height/2 ])
    },
    updatePath() {
      this.path = geoPath()
        .projection(this.projection)
        .context(this.context)
    },
    scrollSetDataOnLoad() {
      const startEl = document.querySelector('.content');
      const endEl = document.querySelector('.map__movement');
      ScrollTrigger.create({
        trigger: startEl,
        start: 'top bottom',
        endTrigger: endEl,
        end: 'top bottom',
        onToggle: ({isActive}) => {
          if (isActive === true) {
            this.config.countries = ['Rwanda'];
            this.config.bridges = ['Gahororo'];
            this.config.villages = [];
            this.config.bridgeTrips = [];
            this.setData();
          }
        },
      })
    },
    scrollSetDataForBridge() {
      const startEl = document.querySelector('.map__movement');
      const endEl = document.querySelector('.map__rwanda');
      ScrollTrigger.create({
        trigger: startEl,
        start: 'top bottom',
        endTrigger: endEl,
        end: 'top bottom',
        onToggle: ({isActive}) => {
          if (isActive === true) {
            this.config.countries  = ['Rwanda'];
            this.config.bridges = ['Gahororo'];
            this.config.villages = this.villagesGahororo;
            this.config.bridgeTrips = ['Gahororo'];
            this.setData();
          }
        },
      })
    },
    scrollSetDataForRwanda() {
      const startEl = document.querySelector('.map__rwanda');
      const endEl = document.querySelector('.map__world');
      ScrollTrigger.create({
        trigger: startEl,
        start: 'top bottom',
        endTrigger: endEl,
        end: 'top bottom',
        onToggle: ({isActive}) => {
          if (isActive === true) {
            delete this.config.countries;
            delete this.config.bridges;
            // this.config.villages = this.villagesGahororo;
            delete this.config.villages;
            delete this.config.bridgeTrips;
            // this.config.bridgeTrips = [];
            this.setData();
          }
        },
      })
    },
    scrollSetDataForWorld() {
      const el = document.querySelector('.map__world');
      ScrollTrigger.create({
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        onToggle: ({isActive}) => {
          if (isActive === true) {
            delete this.config.countries;
            delete this.config.bridges;
            this.config.villages = [];
            this.config.bridgeTrips = [];
            this.setData();
          }
        },
      })
    },
    getPct(start, end, pct) {
      return start + (end - start) * pct;
    },
    initScrollLoadMap() {
      const startEl = document.querySelector('.content');
      const endEl = document.querySelector('.map__intro');
      ScrollTrigger.create({
        trigger: startEl,
        start: 'top bottom',
        endTrigger: endEl,
        end: 'top bottom',
        onRefresh: (self) => {
          if (self.isActive === true) {
            self.progress += .0001;
          }
        },
        onUpdate: () => {
          // starting scale
          this.config.scale = this.rwandaStartScale;
          this.config.center = this.rwandaStartCoords;
          this.draw();
        }
      })
    },
    initScrollFirstBridge() {
      const startEl = document.querySelector('.content');
      const endEl = document.querySelector('.map__intro');

      const pct = {cur: 0}
      const startBridgeRadius = 0;
      let endBridgeRadius;

      gsap.to(pct, {
        cur: 1,
        scrollTrigger: {
          trigger: startEl,
          start: 'top top',
          endTrigger: endEl,
          end: 'bottom top',
          scrub: .5,
          onRefresh: (self) => {
            if (self.isActive === true) {
              self.progress += .0001;
            }
          }
        },
        onUpdate: () => {
          endBridgeRadius = this.bridgeRadius.max;
          this.config.bridgeRadius = this.getPct(startBridgeRadius, endBridgeRadius, pct.cur);
          this.draw();
        }
      })
    },
    initScrollFirstBridgeZoom() {
      // map reveals for the first time; show the first bridge 
      const el = document.querySelector('.map__movement');

      let pct = {first: 0, second: 0},
          startVillageRadius,
          endVillageRadius,
          startX,
          endX,
          startY,
          endY,
          startScale,
          endScale;

      // move the center
      gsap.to(pct, {
        first: 1,
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'top 50%',
          scrub: .5,
          onRefresh: (self) => {
            if (self.isActive === true) {
              self.progress += .0001;
            }
          },
        },
        onUpdate: () => {
          // change XY
          startX = this.rwandaStartCoords[0];
          startY = this.rwandaStartCoords[1];
          endX = this.primaryBridgeCoords[0];
          endY = this.primaryBridgeCoords[1];
          // change XY
          const curX = this.getPct(startX, endX, pct.first);
          const curY = this.getPct(startY, endY, pct.first);
          this.config.center = [curX, curY];
        
          // zoom on the village
          startVillageRadius = 0;
          endVillageRadius = this.villageRadius.max;
          // show the village outline
          this.config.villageRadius = this.getPct(startVillageRadius, endVillageRadius, pct.first/2);

          // draw
          this.draw();
        }
      });

      // add villages in and zoom
      gsap.to(pct, {
        second: 1,
        scrollTrigger: {
          trigger: el,
          start: 'top 50%',
          end: 'bottom top',
          scrub: .5,
          onRefresh: (self) => {
            if (self.isActive === true) {
              self.progress += .0001;
            }
          },
        },
        onUpdate: () => {
          // set scales
          startScale = this.rwandaStartScale;
          endScale = this.primaryBridgeScale;
          // change scale
          this.config.scale = this.getPct(startScale, endScale, pct.second);

          // zoom on the village
          startVillageRadius = 0;
          endVillageRadius = this.villageRadius.max;
          // keep updating village outline
          this.config.villageRadius = this.getPct(startVillageRadius, endVillageRadius, .5 + pct.second/2);

          // draw
          this.draw();
        }
      })
    },
    initScrollHighlightRoutes() {
      const routes = ['healthcare', 'education', 'employment'];

      routes.forEach(route => {
        // set purpose
        const el = document.querySelector('.map__' + route);
        ScrollTrigger.create({
          trigger: el,
          start: 'top bottom',
          onToggle: ({isActive}) => {
            if (isActive) {
              this.highlightPurpose(route);
            }
          },
          onLeave: () => {
            if (route === 'employment') {
              this.highlightPurpose('showSome');
            }
          },
          onLeaveBack: () => {
            if (route === 'healthcare') {
              this.highlightPurpose('showAll');
            }
          }
        });
      })
    },
    initScrollHighlightVillages() {

      const el = document.querySelector('.map__connection');

      let pct = {cur: 0};

      gsap.to(pct, {
        cur: 1,
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'top 50%',
          scrub: .5,
        },
        onUpdate: () => {
          this.config.highlightRadiusPct = pct.cur;
        }
      });
    },
    initScrollHighlightRoutesAll() {
      const el = document.querySelector('.map__rwanda');
      
      let pct = {first: 0, second: .5},
          startScale,
          midScale,
          endScale,
          startBridgeRadius,
          endBridgeRadius,

          startVillageRadius,
          endVillageRadius,
          
          startTripRadius,
          endTripRadius,
          endX, endY,
          startX, startY,
          curX, curY;

      // zoom out to midRwandaScale and start to reduce linewidths
      gsap.to(pct, {
        first: .5,
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'top 50%',
          scrub: .5,
          onRefresh: (self) => {
            if (self.isActive === true) {
              self.progress += .0001;
            }
          },
        },
        onUpdate: () => {
          // change scale
          startScale = this.primaryBridgeScale;
          midScale = this.midRwandaScale;
          // zoom out
          this.config.scale = this.getPct(startScale, midScale, 2*pct.first);

          // change bridge circle size
          startBridgeRadius = this.bridgeRadius.max;
          endBridgeRadius = this.bridgeRadius.min;
          // change bridge circle - halfway
          this.config.bridgeRadius = this.getPct(startBridgeRadius, endBridgeRadius, pct.first);

          // change village bubble
          this.config.highlightRadiusPct = this.getPct(1, 0, pct.first);

          // village size
          startVillageRadius = this.villageRadius.max;
          endVillageRadius = this.villageRadius.min;
          // change village marker - halfway
          this.config.villageRadius = this.getPct(startVillageRadius, endVillageRadius, pct.first);

          // change trip size
          startTripRadius = this.tripRadius.max;
          endTripRadius = this.tripRadius.min;
          // change trip size - halfway
          this.config.tripRadius = this.getPct(startTripRadius, endTripRadius, pct.first);

          // draw
          // this.draw();
        }
      });


      // zoom to endRwanda Scale and keep reducing line widths
      // start to move center
      gsap.to(pct, {
        second: 1,
        scrollTrigger: {
          trigger: el,
          start: 'top 50%',
          end: 'bottom top',
          scrub: .5,
          onRefresh: (self) => {
            if (self.isActive === true) {
              self.progress += .0001;
            }
          },
        },
        onUpdate: () => {
          // change scale
          midScale = this.midRwandaScale;
          endScale = this.rwandaEndScale;
          // zoom out more
          this.config.scale = this.getPct(midScale, endScale, pct.second);

          // change bridge circle size
          startBridgeRadius = this.bridgeRadius.max;
          endBridgeRadius = this.bridgeRadius.min;
          // change bridge circle - halfway
          this.config.bridgeRadius = this.getPct(startBridgeRadius, endBridgeRadius, pct.second);
          

          // change village marker - halfway
          startVillageRadius = this.villageRadius.max;
          endVillageRadius = this.villageRadius.min;
          this.config.villageRadius = this.getPct(startVillageRadius, endVillageRadius, pct.second);

          // change village bubble
          this.config.highlightRadiusPct = this.getPct(1, 0, pct.second);

          // change trip size
          startTripRadius = this.tripRadius.max;
          endTripRadius = this.tripRadius.min;
          // change trip size - halfway
          this.config.tripRadius = this.getPct(startTripRadius, endTripRadius, pct.second);

          // move center
          // change xy
          startX = this.primaryBridgeCoords[0];
          startY= this.primaryBridgeCoords[1];
          endX = this.rwandaEndCoords[0];
          endY = this.rwandaEndCoords[1]; 
          curX = this.getPct(startX, endX, (pct.second-.5)*2);
          curY = this.getPct(startY, endY, (pct.second-.5)*2);
          this.config.center = [curX, curY];

          // draw
        }
      });
    },
    initScrollWorld() {
      const el = document.querySelector('.map__world');

      let pct = {first: 0, second: .5},
          startWidth, endWidth,
          startScale, midScale, endScale,
          startX, curX, endX,
          startY, curY, endY;


      // first move
      gsap.to(pct, {
        first: .5,
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'bottom 50%',
          scrub: .5,
          onRefresh: (self) => {
            if (self.isActive === true) {
              self.progress += .0001;
            }
          },          
        },
        onUpdate: () => {
          // scale
          startScale = this.rwandaEndScale;
          midScale = this.midEarthScale;
          this.config.scale = this.getPct(startScale, midScale, pct.first*2); // double it so you get all the way there

          // line width
          startWidth = this.countryLineWidth.max;
          endWidth = this.countryLineWidth.min;
          this.config.countryLineWidth = this.getPct(startWidth, endWidth, pct.first);

          // draw
          this.draw();
        }
      })

      // second
      gsap.to(pct, {
        second: 1,
        scrollTrigger: {
          trigger: el,
          start: 'bottom 50%',
          end: 'bottom top',
          scrub: .5,
          onRefresh: (self) => {
            if (self.isActive === true) {
              self.progress += .0001;
            }
          },          
        },
        onUpdate: () => {
          // line width
          startWidth = this.countryLineWidth.max;
          endWidth = this.countryLineWidth.min;
          this.config.countryLineWidth = this.getPct(startWidth, endWidth, pct.second);

          // scale
          midScale = this.midEarthScale;
          endScale = this.earthScale;
          this.config.scale = this.getPct(midScale, endScale, (pct.second-.5)*2); // double it so you get all the way there

          // center
          startX = this.rwandaEndCoords[0];
          startY = this.rwandaEndCoords[1];
          endX = this.earthCoords[0];
          endY = this.earthCoords[1];
          curX = this.getPct(startX, endX, (pct.second-.5)*2); //doubles
          curY = this.getPct(startY, endY, (pct.second-.5)*2);
          this.config.center = [curX, curY]

          this.draw();
        }
      })
    },
    initScrollTrips() {
      const startEl = document.querySelector('.map__movement');
      const endEl = document.querySelector('.map__world');

      let vars = {
        startProgress: 0,
        endProgress: 1,
      };

      gsap.to(vars, {
        startProgress: vars.endProgress,
        duration: 1,
        repeat: -1,
        ease: 'power0',
        scrollTrigger: {
          trigger: startEl,
          start: 'top bottom',
          endTrigger: endEl,
          end: 'top bottom',
          toggleActions: 'play pause resume pause',
        },
        onEnter: () => {
          this.highlightPurpose('showAll');
        },
        onUpdate: () => {
          // update path
          this.updateTrips(vars.startProgress);
          // draw
          this.draw();
        }
      })
    },
    highlightPurpose(purpose) {

      // LEGEND
      const spanishPurpose = {
        healthcare: 'atención sanitaria',
        education: 'educación',
        employment: 'empleo'
      }
      this.currentTripPurpose = purpose;
      this.currentTripPurposeEs = spanishPurpose[purpose];

      const classDict = {
        healthcare: 'has-text-red',
        education: 'has-text-green',
        employment: 'has-text-light-blue'
      }
      this.currentTripClass = classDict[purpose];


      // DATA
      // backup color
      const defaultColor = 'rgba(22, 19, 69, .1)';

      // invisible color
      const invisibleColor = 'rgba(0,0,0,0)';
      
      // color dict
      const colorDict = {
        healthcare:   'rgba(234,113, 73, .33)',
        education:    'rgba(  0,145, 73, .33)',
        employment:   'rgba(102,195,227, .33)'
      };


      // consts
      let tripColor = defaultColor;


      // apply color and save
      for (let i=0; i<this.data.trips.length; i++) {
        const trip = Object.assign({}, this.data.trips[i]);

        switch (purpose) {
          case 'showNone':
            tripColor = invisibleColor;
            break;
          case 'showSome':
            tripColor = defaultColor;
            break;
          case 'showAll':
            tripColor = defaultColor;
            break;
          default:
            if (trip.purpose === purpose) {
              tripColor = colorDict[purpose];
            } else {
              tripColor = invisibleColor;
            }
        }
        this.data.trips[i].fillColor = tripColor;
      }
    }
  },
}

</script>